import { useState, useEffect } from 'react';
import api, { downloadCsv } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import StateBlock from '../components/StateBlock';
import CsvImportModal from '../components/CsvImportModal';

export default function ExpensesPage() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState(null);
  const [error, setError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState({ expense_date: '', amount: '', description: '', bank_reference: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  function load() {
    setError(false);
    api.get('/expenses').then(r => setExpenses(r.data)).catch(() => setError(true));
  }
  useEffect(load, []);

  async function handleSubmit() {
    if (!form.expense_date || !form.amount) {
      setFormError('Date and amount are required');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await api.post('/expenses', { ...form, amount: Number(form.amount) });
      setForm({ expense_date: '', amount: '', description: '', bank_reference: '', notes: '' });
      setShowForm(false);
      load();
    } catch (e) {
      setFormError(e.response?.data?.error || 'Could not save expense');
    } finally {
      setSaving(false);
    }
  }

  async function handleExport() {
    await downloadCsv('/expenses/export', 'expenses-export.csv');
  }

  function fmt(n) { return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`; }
  const total = expenses ? expenses.reduce((sum, e) => sum + e.amount, 0) : 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Expenses {expenses && <span className="page-title-count">({expenses.length} total, {fmt(total)})</span>}</h1>
          <p className="page-subtitle">TMV's own operating expenses — separate from referral/commission payouts under People.</p>
        </div>
        <div className="page-actions">
          {user?.role === 'admin' && (
            <button className="btn btn-secondary" onClick={handleExport}>↓ Export CSV</button>
          )}
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>↑ Upload CSV</button>
          <button className="btn btn-primary" onClick={() => setShowForm(s => !s)}>+ Record Expense</button>
        </div>
      </div>

      {showForm && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20 }}>
          {formError && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>{formError}</div>}
          <div className="form-grid">
            <div className="form-field"><label>Date</label><input type="date" value={form.expense_date} onChange={e => setForm(f => ({ ...f, expense_date: e.target.value }))} /></div>
            <div className="form-field"><label>Amount</label><input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
            <div className="form-field span-2"><label>Description</label><input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
            <div className="form-field span-2"><label>Bank reference</label><input value={form.bank_reference} onChange={e => setForm(f => ({ ...f, bank_reference: e.target.value }))} /></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && expenses === null && <StateBlock type="loading" />}
      {!error && expenses !== null && expenses.length === 0 && <StateBlock type="empty" title="No expenses recorded yet" />}

      {!error && expenses && expenses.length > 0 && (
        <table className="data-table">
          <thead><tr><th>Date</th><th>Description</th><th>Amount</th><th>Bank Ref</th></tr></thead>
          <tbody>
            {expenses.map(e => (
              <tr key={e.id}>
                <td>{e.expense_date}</td>
                <td>{e.description || '—'}</td>
                <td>{fmt(e.amount)}</td>
                <td>{e.bank_reference || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showImport && (
        <CsvImportModal entityLabel="Expense" importPath="/expenses/import" onClose={() => setShowImport(false)} onComplete={load} />
      )}
    </div>
  );
}
